import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import LogoMark from '../components/LogoMark';
import api, { formatApiErrorDetail } from '../api';

/**
 * 01 — Onboarding (invite / referral code entry)
 * Matches Claude's 01-onboarding.html: 6 color-coded boxes, gradient tagline,
 * bonding-curve bubble illustration. Verifies code against backend.
 *
 * A shared invite link ("Copy full invite link" on /referrals) points to
 * `/onboarding?code=XXXXXX`. If that param is present, we pre-fill the 6 boxes
 * and auto-verify so the referee doesn't have to retype the code.
 */
export default function Onboarding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const initialCode = (searchParams.get('code') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  const initialChars = Array.from({ length: 6 }, (_, i) => initialCode[i] || '');

  const [chars, setChars] = useState(initialChars);
  const [hint, setHint] = useState({ text: t('onboarding.caseInsensitiveNote'), kind: 'muted' });
  const [busy, setBusy] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(searchParams.get('waitlist') === '1');
  const inputs = useRef([]);
  const navigate = useNavigate();

  const filled = chars.every((c) => c.length === 1);
  const code = chars.join('').toUpperCase();

  useEffect(() => {
    // If we arrived with a valid ?code= param, verify + advance automatically so
    // the referee never has to re-type. Otherwise focus the first empty box.
    if (initialCode.length === 6) {
      // Strip the query param so refreshes don't re-trigger the auto flow
      const p = new URLSearchParams(searchParams);
      p.delete('code');
      setSearchParams(p, { replace: true });
      setHint({ text: '✓ Invite code applied from link', kind: 'success' });
      autoVerify(initialCode);
    } else {
      // Truncated/empty code: focus first empty box so user can complete
      inputs.current[initialCode.length]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoVerify = async (fullCode) => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/verify-code', { code: fullCode });
      if (data.valid) {
        setHint({ text: '✓ Code accepted', kind: 'success' });
        setTimeout(
          () => navigate('/signup', { state: { code: fullCode, referrer: data.referrer || null } }),
          500
        );
      } else {
        setHint({ text: data.reason || "That invite code isn't valid anymore.", kind: 'error' });
        setBusy(false);
        inputs.current[0]?.focus();
      }
    } catch (e) {
      setHint({ text: formatApiErrorDetail(e.response?.data?.detail) || 'Could not verify. Try again.', kind: 'error' });
      setBusy(false);
      inputs.current[0]?.focus();
    }
  };

  const handleChange = (i, val) => {
    const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
    const next = [...chars];
    next[i] = clean;
    setChars(next);
    if (clean && i < 5) inputs.current[i + 1]?.focus();
    setHint({ text: t('onboarding.caseInsensitiveNote'), kind: 'muted' });
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !chars[i] && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === 'Enter' && filled) handleContinue();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData('text') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    const next = [...chars];
    text.split('').forEach((ch, idx) => { next[idx] = ch; });
    setChars(next);
    const nextEmpty = next.findIndex((c) => !c);
    inputs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  const handleContinue = async () => {
    if (!filled || busy) return;
    setBusy(true);
    setHint({ text: 'Verifying code…', kind: 'muted' });
    try {
      const { data } = await api.post('/auth/verify-code', { code });
      if (data.valid) {
        setHint({ text: '✓ Code accepted', kind: 'success' });
        // Pass code + referrer info forward to sign-up
        setTimeout(
          () => navigate('/signup', { state: { code, referrer: data.referrer || null } }),
          300
        );
      } else {
        setHint({ text: data.reason || "That code doesn't look right.", kind: 'error' });
        setBusy(false);
      }
    } catch (e) {
      setHint({ text: formatApiErrorDetail(e.response?.data?.detail) || 'Could not verify. Try again.', kind: 'error' });
      setBusy(false);
    }
  };

  return (
    <>
      <BlobField />
      <div className="stage">
        <div className="card">
          <LogoMark size={64} />
          <p className="hero-tagline">{t('onboarding.heroLine1')}<br />{t('onboarding.heroLine2')}</p>

          <div className="eyebrow">{t('onboarding.inviteOnly')}</div>
          <p className="subcopy">{t('onboarding.invitePrompt')}</p>

          <div className="curve-chip">
            <div className="curve-chip-label">{t('onboarding.bitPriceCaption')}</div>
            <div className="curve-row">
              <div className="curve-bubble" />
              <div className="curve-bubble" />
              <div className="curve-bubble" />
              <div className="curve-bubble" />
              <div className="curve-bubble" />
              <div className="curve-bubble" />
            </div>
            <div className="curve-caption">
              <span>{t('onboarding.firstBit')}</span>
              <span>{t('onboarding.hundredthBit')}</span>
            </div>
          </div>

          <label className="code-label" htmlFor="code-0">{t('onboarding.codeLabel')}</label>
          <div className="code-boxes" data-testid="invite-code-boxes">
            {chars.map((ch, i) => (
              <input
                key={i}
                id={`code-${i}`}
                data-testid={`invite-code-input-${i}`}
                ref={(el) => (inputs.current[i] = el)}
                className={`code-box c${i}${ch ? ' filled' : ''}`}
                maxLength={1}
                inputMode="text"
                autoComplete="off"
                value={ch}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                aria-label={`Character ${i + 1}`}
              />
            ))}
          </div>
          <p className={`code-hint${hint.kind === 'error' ? ' error' : hint.kind === 'success' ? ' success' : ''}`} data-testid="invite-code-hint">
            {hint.text}
          </p>

          <button
            className="btn-primary"
            data-testid="invite-code-continue-btn"
            disabled={!filled || busy}
            onClick={handleContinue}
          >
            <span>{t('common.continue')}</span>
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="divider-row"><div className="line" /><span>{t('onboarding.orDivider')}</span><div className="line" /></div>

          <button
            className="request-link"
            data-testid="request-access-btn"
            onClick={() => setShowWaitlist(true)}
          >
            {t('onboarding.noCode')} <b>{t('onboarding.requestAccess')}</b>
          </button>

          <p className="signin-note">
            {t('onboarding.alreadyHaveAccount')} <Link to="/login" data-testid="signin-link">{t('common.signIn')}</Link>
          </p>
        </div>
      </div>

      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
    </>
  );
}

function WaitlistModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [socials, setSocials] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!email || busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.post('/waitlist', { email, socials });
      setSent(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || 'Please enter a valid email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="waitlist-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="waitlist-close-btn">×</button>
        {!sent ? (
          <>
            <h2 className="heading" style={{ textAlign: 'left' }}>Join the <span className="grad">waitlist</span></h2>
            <p className="subcopy" style={{ textAlign: 'left', margin: '0 0 22px' }}>
              Clanster is invite-only during beta. Drop your email and we'll send a code as spots open up.
            </p>
            <div className="field">
              <label className="field-label"><span>Email</span></label>
              <input
                type="email"
                className="field-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email-input"
              />
            </div>
            <div className="field">
              <label className="field-label"><span>Socials (optional)</span></label>
              <input
                type="text"
                className="field-input"
                placeholder="@yourhandle on X / Insta / YouTube"
                value={socials}
                onChange={(e) => setSocials(e.target.value)}
                data-testid="waitlist-socials-input"
              />
            </div>
            {err && <p style={{ color: 'var(--error)', fontSize: 13, margin: '4px 0 12px' }} data-testid="waitlist-error">{err}</p>}
            <button
              className="btn-primary"
              onClick={submit}
              disabled={!email || busy}
              data-testid="waitlist-submit-btn"
            >
              {busy ? 'Saving…' : 'Join Waitlist'}
            </button>
          </>
        ) : (
          <div className="success-wrap">
            <div className="check-circle">
              <svg viewBox="0 0 24 24" fill="none" width="32" height="32">
                <path d="M5 13l5 5L20 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="success-title">You're on the list</div>
            <p className="success-sub">
              We'll email <span className="success-email">{email}</span> as soon as a spot opens.
            </p>
            <button className="btn-secondary" onClick={onClose} data-testid="waitlist-done-btn">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
